import { useCallback, useEffect, useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import Modal from "@/components/base/Modal";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { supabase } from "@/store/shared/supabase";
import { formatDate } from "@/utils/format";
import type { ExportColumn } from "@/utils/export";

type StatutEssai = "actif" | "expire_bientot" | "expire" | "converti" | "inconnu";

interface EssaiRow {
  identite: string;
  compte_id: string | null;
  compte_nom: string | null;
  compte_email: string | null;
  role: string | null;
  compte_statut: string | null;
  started_at: string | null;
  ended_at: string | null;
  abonnement_id: string | null;
  est_essai: boolean | null;
  abo_statut: string | null;
  date_fin: string | null;
  jours_restants: number | null;
  statut_essai: StatutEssai;
}

const STATUT_LABELS: Record<StatutEssai, string> = {
  actif: "Actif",
  expire_bientot: "Expire bientôt",
  expire: "Expiré",
  converti: "Converti (payant)",
  inconnu: "Indéterminé",
};

const STATUT_TONES: Record<StatutEssai, "success" | "accent" | "danger" | "secondary" | "neutral"> = {
  actif: "success",
  expire_bientot: "accent",
  expire: "danger",
  converti: "secondary",
  inconnu: "neutral",
};

const ROLE_LABELS: Record<string, string> = {
  dg_proprietaire: "Propriétaire",
  dg_agence: "Agence",
};

function joursLabel(jours: number | null): string {
  if (jours === null) return "—";
  if (jours < 0) return `Expiré depuis ${Math.abs(jours)} j`;
  if (jours === 0) return "Dernier jour";
  return `${jours} j`;
}

async function fetchEssais(): Promise<EssaiRow[]> {
  const { data, error } = await supabase.rpc("lister_essais_super_admin");
  if (error) throw error;
  return (data as EssaiRow[] | null) ?? [];
}

export default function SuperAdminEssaisPage() {
  const [rows, setRows] = useState<EssaiRow[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");

  // Prolongation exceptionnelle
  const [cible, setCible] = useState<EssaiRow | null>(null);
  const [jours, setJours] = useState("30");
  const [justification, setJustification] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreurProlongation, setErreurProlongation] = useState("");

  const charger = useCallback(async () => {
    try {
      setChargement(true);
      setErreur(null);
      setRows(await fetchEssais());
    } catch {
      setErreur("Le chargement du suivi des essais a échoué. Réessayez.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      actifs: rows.filter((r) => r.statut_essai === "actif").length,
      bientot: rows.filter((r) => r.statut_essai === "expire_bientot").length,
      expires: rows.filter((r) => r.statut_essai === "expire").length,
      convertis: rows.filter((r) => r.statut_essai === "converti").length,
    }),
    [rows]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statutFilter && row.statut_essai !== statutFilter) return false;
      if (term) {
        const haystack = `${row.compte_nom ?? ""} ${row.compte_email ?? ""} ${row.identite}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [rows, search, statutFilter]);

  const exportColumns: ExportColumn<EssaiRow>[] = [
    { header: "Compte", value: (row) => row.compte_nom ?? "" },
    { header: "E-mail", value: (row) => row.compte_email ?? row.identite },
    { header: "Rôle", value: (row) => ROLE_LABELS[row.role ?? ""] ?? row.role ?? "" },
    { header: "Début essai", value: (row) => row.started_at ?? "" },
    { header: "Fin essai", value: (row) => row.date_fin ?? "" },
    { header: "Jours restants", value: (row) => row.jours_restants ?? "" },
    { header: "Statut", value: (row) => STATUT_LABELS[row.statut_essai] },
  ];

  const columns: DataTableColumn<EssaiRow>[] = [
    {
      key: "compte",
      header: "Compte",
      className: "min-w-[200px]",
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-label font-medium text-foreground-950">{row.compte_nom ?? "Compte supprimé"}</span>
          <span className="text-xs text-foreground-500">{row.compte_email ?? row.identite}</span>
        </div>
      ),
    },
    {
      key: "role",
      header: "Type",
      className: "min-w-[110px]",
      render: (row) => <span className="text-foreground-700">{ROLE_LABELS[row.role ?? ""] ?? "—"}</span>,
    },
    {
      key: "debut",
      header: "Début",
      className: "min-w-[120px]",
      render: (row) => <span className="text-foreground-700">{row.started_at ? formatDate(row.started_at) : "—"}</span>,
    },
    {
      key: "fin",
      header: "Fin",
      className: "min-w-[120px]",
      render: (row) => <span className="text-foreground-700">{row.date_fin ? formatDate(row.date_fin) : "—"}</span>,
    },
    {
      key: "jours",
      header: "Échéance",
      align: "center",
      className: "min-w-[120px]",
      render: (row) => (
        <span className="font-label font-medium text-foreground-950">{joursLabel(row.jours_restants)}</span>
      ),
    },
    {
      key: "statut",
      header: "Statut",
      className: "min-w-[150px]",
      render: (row) => <Badge tone={STATUT_TONES[row.statut_essai]}>{STATUT_LABELS[row.statut_essai]}</Badge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[140px]",
      render: (row) => (
        <button
          type="button"
          disabled={!row.compte_id || row.statut_essai === "converti"}
          onClick={() => {
            setCible(row);
            setJours("30");
            setJustification("");
            setErreurProlongation("");
          }}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        >
          <i className="ri-calendar-check-line text-base" aria-hidden="true" />
          Prolonger
        </button>
      ),
    },
  ];

  const validerProlongation = async () => {
    if (!cible?.compte_id) return;
    const nbJours = Number.parseInt(jours, 10);
    if (!Number.isFinite(nbJours) || nbJours < 1 || nbJours > 365) {
      setErreurProlongation("Indiquez une durée entre 1 et 365 jours.");
      return;
    }
    if (!justification.trim()) {
      setErreurProlongation("La justification est obligatoire.");
      return;
    }
    try {
      setEnvoi(true);
      setErreurProlongation("");
      const { error } = await supabase.rpc("prolonger_essai", {
        p_compte_id: cible.compte_id,
        p_jours: nbJours,
        p_justification: justification.trim(),
      });
      if (error) throw error;
      setCible(null);
      await charger();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setErreurProlongation(
        message && message.includes("essai")
          ? message
          : "La prolongation a échoué. Vérifiez la durée et réessayez."
      );
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Essais gratuits</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Suivi de l'offre de lancement : essais actifs, à venir à expiration, expirés et convertis en abonnement
            payant. Une identité (e-mail) ne peut bénéficier que d'un seul essai, même après recréation d'un compte.
          </p>
        </div>
        <ExportMenu rows={filtered} columns={exportColumns} filename="super-admin-essais" title="Essais gratuits" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard icon="ri-gift-2-line" label="Essais" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-time-line" label="Expirent bientôt" value={String(stats.bientot)} tone="accent" />
        <StatCard icon="ri-error-warning-line" label="Expirés" value={String(stats.expires)} tone="neutral" />
        <StatCard icon="ri-exchange-dollar-line" label="Convertis" value={String(stats.convertis)} tone="secondary" />
      </div>

      {rows.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="essai-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <div className="relative">
                <i
                  className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"
                  aria-hidden="true"
                />
                <input
                  id="essai-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Compte, e-mail..."
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="essai-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <select
                id="essai-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                className="h-[42px] w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Tous les statuts</option>
                {(Object.keys(STATUT_LABELS) as StatutEssai[]).map((statut) => (
                  <option key={statut} value={statut}>
                    {STATUT_LABELS[statut]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>
      )}

      {chargement && rows.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void charger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && rows.length === 0 && (
        <EmptyState
          icon="ri-gift-2-line"
          title="Aucun essai gratuit"
          description="Les essais gratuits accordés aux nouveaux propriétaires et agences apparaîtront ici, avec leur date de fin et leur statut."
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(row) => row.identite}
          minWidthClass="min-w-[1050px]"
          caption="Essais gratuits"
        />
      )}

      <Modal
        open={Boolean(cible)}
        onClose={() => setCible(null)}
        size="md"
        title="Prolongation exceptionnelle de l'essai"
      >
        {cible && (
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-700">
              <p>
                Compte : <strong className="text-foreground-950">{cible.compte_nom ?? cible.identite}</strong>
              </p>
              <p className="mt-0.5 text-xs text-foreground-500">
                Fin actuelle : {cible.date_fin ? formatDate(cible.date_fin) : "—"}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="prolong-jours" className="text-sm font-label font-medium text-foreground-800">
                Durée de la prolongation (jours)
              </label>
              <input
                id="prolong-jours"
                type="number"
                min={1}
                max={365}
                value={jours}
                onChange={(event) => setJours(event.target.value)}
                className="h-[42px] w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="prolong-justif" className="text-sm font-label font-medium text-foreground-800">
                Justification <span className="text-primary-600">*</span>
              </label>
              <textarea
                id="prolong-justif"
                rows={3}
                maxLength={500}
                value={justification}
                onChange={(event) => setJustification(event.target.value)}
                placeholder="Motif de la prolongation (enregistré dans le journal d'audit)."
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              <span className="text-right text-[11px] text-foreground-400">{justification.length}/500</span>
            </div>

            {erreurProlongation && (
              <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3.5 py-2.5 text-xs text-primary-800">
                <i className="ri-error-warning-line text-sm" aria-hidden="true" />
                {erreurProlongation}
              </p>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setCible(null)}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                Annuler
              </button>
              <Button type="button" onClick={() => void validerProlongation()} disabled={envoi}>
                <i className="ri-calendar-check-line" aria-hidden="true" />
                {envoi ? "Prolongation…" : "Prolonger l'essai"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}